import React from "react";
import styled, { css } from "styled-components";

import { Divider } from "js/Components/Divider";

const thumbnailCardSelectedStyles = css`
    border: 1px solid #11A9E2;
    background: #D5E6ED;

    :hover {
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.01), 0 2px 2px 0 rgba(0, 0, 0, 0.01), 0 4px 4px 0 rgba(0, 0, 0, 0.02), 0 8px 8px 0 rgba(0, 0, 0, 0.03), 0 14px 14px 0 rgba(0, 0, 0, 0.03);
        border: 1px solid #11A9E2;
        background: #D5E6ED;
    }
`;

const thumbnailCardDefaultStyles = css`
    border: 1px solid #DDD;
    background: #FFF;

    :hover {
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.01), 0 2px 2px 0 rgba(0, 0, 0, 0.01), 0 4px 4px 0 rgba(0, 0, 0, 0.02), 0 8px 8px 0 rgba(0, 0, 0, 0.03), 0 14px 14px 0 rgba(0, 0, 0, 0.03);
        background: #F6F9FB;
        cursor: pointer;
    }
`;

export const thumbnailMenuButtonStyles = css`
    background-color: rgba(255, 255, 255, 0.90);
    padding: 2px;
    border-radius: 4px;
    border: 1px solid #DDD;
    font-size: 16px;

    &:hover {
        background-color: white;
    }
`;

const ThumbnailCardContainer = styled.div<{ selected?: boolean }>`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: center;
    justify-content: center;
    gap: 0;
    border-radius: 2px;
    overflow: auto;

    ${props => (props.selected ? thumbnailCardSelectedStyles : thumbnailCardDefaultStyles)}
`;

const ThumbnailCardMenuWrap = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    border-radius: 4px;
    z-index: 1001;
`;

const ThumbnailCardBar = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 42px;
    margin: 9px 0px 10px 0px;

    > div {
        height: 21px;
        display: flex;
        align-items: center;
        padding: 0 10px;
    }

    label {
        font-size: 14px;
        letter-spacing: 0px;
        font-weight: 600;
        color: #222;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex-grow: 2;
        text-transform: none;
        margin: 0px;
    }
`;

export interface ThumbnailCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
    menuOverlay?: React.ReactNode;
    barContent?: React.ReactNode;
    barVariant?: "default" | "custom";
    selected?: boolean;
    children?: React.ReactNode;
}

/**
 * Shared card layout for resource thumbnails (themes, team slides, assets).
 * Renders: container | optional menu overlay | thumbnail content | divider | optional bar.
 */
export const ThumbnailCard = React.forwardRef<HTMLDivElement, ThumbnailCardProps>(
    function ThumbnailCard(
        { menuOverlay, barContent, barVariant = "default", selected, children, ...containerProps },
        ref
    ) {
        const defaultBarContent = (
            <ThumbnailCardBar>
                <div>{barContent}</div>
            </ThumbnailCardBar>
        );

        return (
            <ThumbnailCardContainer ref={ref} selected={selected} {...containerProps}>
                {menuOverlay && (
                    <ThumbnailCardMenuWrap
                        onMouseDown={e => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onMouseUp={e => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {menuOverlay}
                    </ThumbnailCardMenuWrap>
                )}
                {children}
                <Divider color="#DDD" />
                {barContent && (barVariant === "default" ? defaultBarContent : barContent)}
            </ThumbnailCardContainer>
        );
    }
);
